import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { isValid, addMonths, startOfMonth } from 'date-fns'
import _ from 'lodash'
import CafService from '@/api/CafService.js'
import UnitTypeService from '@/api/UnitTypeService.js'
import ContractService from '@/api/ContractService.js'
import { useUserStore } from './user'
import { useOptionsStore } from '@/stores/options'
import { useModalStore } from '@/stores/modal'
import { isActAsOemActive } from '@/helpers/actAsOem'
import { trackTimeSince } from '@/config/analytics'
import { trackHotjarEvent } from '@/config/hotjar'
import { LOCALE_NAMES } from '@/helpers/localeOptions'
import { PRODUCT_TYPE_NAMES, PLANNED_UTILIZATION_YEARLY_DECIMALS } from '@/data/constants'

export const FORM_STATUS = {
    incomplete: 'incomplete',
    valid: 'valid',
    calculated: 'calculated',
}

const MONTHS_IN_YEAR = 12

const FLEXIBILITY_LABEL_KEYS = {
    25: '§caf.flexibility_low',
    50: '§caf.flexibility_medium',
    75: '§caf.flexibility_high',
}

const VALIDITY_KEYS_BY_PRODUCT = {
    [PRODUCT_TYPE_NAMES.PPU]: [
        'isAmountValid',
        'isExpectedUtilizationValid',
        'isCountryValid',
        'isDurationValid',
        'isBuyBackValid',
        'isResidualValueValid',
    ],
    [PRODUCT_TYPE_NAMES.FIXED]: [
        'isAmountValid',
        'isCountryValid',
        'isDurationValid',
        'isBuyBackValid',
        'isResidualValueValid',
    ],
    [PRODUCT_TYPE_NAMES.SLB]: [
        'isAmountValid',
        'isCountryValid',
        'isDurationValid',
        'isBuyBackValid',
        'isResidualValueValid',
    ],
}

export const useCafStore = defineStore('caf', {
    state: () => ({
        activeProductType: null,

        sharedInputs: {
            begin: '',
            net_list_price: null,
            currency_code: null,
            preferred_number_format: null,
            extra_price: null,
            discount: null,
            down_payment: null,
            amount: null,
            duration: null,
            buy_back: null,

            unit_type: null,
            unit_type_name: '',
            residual_value: null,
            stored_residual_value: null,
            residual_value_type: null,

            client_select: null,
            client_select_name: '',
            operator_country_id: null,

            maintenance_cost_show_as: 'line_item',
            transportation: null,
            installation_fee: null,
            maintenance: null,
        },

        [PRODUCT_TYPE_NAMES.PPU]: {
            billing_unit: null,
            billing_unit_decimals: 2,
            unit_type_max_usage: null,
            unit_type_planned_usage: null,
            unit_type_billing_units: null,
            expected_utilization: null,
            expected_utilization_by_month: [],
            consumable_cost: null,
            consumables_cost_show_as: 'ppu_fee',
            revenue_per_wash: null,
            payment_per_piece_decimals: 2,
        },

        formStatus: FORM_STATUS.incomplete,

        sectionValidity: {
            isAmountValid: false,
            isExpectedUtilizationValid: false,
            isExpectedUtilizationByMonthValid: false,
            isCountryValid: false,
            isDurationValid: false,
            isBuyBackValid: false,
            isResidualValueValid: true,
        },

        calculation: {
            [PRODUCT_TYPE_NAMES.PPU]: { split_results: null },
            [PRODUCT_TYPE_NAMES.FIXED]: { time_fee_per_m: null },
            [PRODUCT_TYPE_NAMES.SLB]: { time_fee_per_m: null },
        },

        residualValue: null,

        additionalFormErrors: [],

        calculateId: 0,

        contractId: null,
        savedInputsKey: null,

        formDefaultsApplied: false,

        calculating: false,
        calculatingResidualValue: false,
        loading: false,
        saving: false,
        downloading: false,

        focusHandlers: {},
    }),

    getters: {
        currencyCode(state) {
            return (
                state.sharedInputs.currency_code ||
                this._userStore().userData.currency_code ||
                'EUR'
            )
        },

        ppuSplitRows(state) {
            const splitResults = state.calculation[PRODUCT_TYPE_NAMES.PPU].split_results || {}

            return Object.entries(splitResults)
                .map(([key, value]) => {
                    const percentage = Math.round(parseFloat(key) * 100)
                    return {
                        key,
                        labelKey: FLEXIBILITY_LABEL_KEYS[percentage] ?? null,
                        percentage,
                        time_fee_per_m: value.time_fee_per_m,
                        display_price_per_h: value.display_price_per_h,
                    }
                })
                .sort((a, b) => b.percentage - a.percentage)
        },

        monthlyFee(state) {
            if (this.isFixedProductType) {
                return state.calculation[PRODUCT_TYPE_NAMES.FIXED].time_fee_per_m
            }
            if (this.isSlbProductType) {
                return state.calculation[PRODUCT_TYPE_NAMES.SLB].time_fee_per_m
            }
            return null
        },

        hasCurrentContract(state) {
            return (
                state.contractId !== null &&
                JSON.stringify(this.buildSaveInputs()) === state.savedInputsKey
            )
        },

        isOperatorSelected(state) {
            return state.sharedInputs.client_select !== null
        },

        errorTypeMaxUsageExceeded() {
            if (
                this.activeProductTypeLabel === PRODUCT_TYPE_NAMES.PPU &&
                this.sharedInputs.unit_type !== null &&
                this[PRODUCT_TYPE_NAMES.PPU] &&
                this[PRODUCT_TYPE_NAMES.PPU].unit_type_max_usage !== null &&
                Number(this[PRODUCT_TYPE_NAMES.PPU].unit_type_max_usage) <
                    Number(this[PRODUCT_TYPE_NAMES.PPU].expected_utilization)
            ) {
                return true
            }

            return false
        },

        warnTypePlannedUsageExceeded() {
            if (
                this.sharedInputs.unit_type !== null &&
                this[PRODUCT_TYPE_NAMES.PPU] &&
                Number(this[PRODUCT_TYPE_NAMES.PPU].unit_type_planned_usage) <
                    Number(this[PRODUCT_TYPE_NAMES.PPU].expected_utilization)
            ) {
                return true
            }

            return false
        },

        isPlannedUtilizationSetByMonth(state) {
            const { expected_utilization_by_month } = state[PRODUCT_TYPE_NAMES.PPU]
            return expected_utilization_by_month && expected_utilization_by_month.length > 0
        },

        isPlannedUtilizationSetByMonthValid(state) {
            const { expected_utilization_by_month } = state[PRODUCT_TYPE_NAMES.PPU]
            const { duration } = state.sharedInputs
            return (
                this.isPlannedUtilizationSetByMonth &&
                expected_utilization_by_month.length === duration * MONTHS_IN_YEAR
            )
        },

        getAverageUtilizationPerYear(state) {
            const { expected_utilization_by_month } = state[PRODUCT_TYPE_NAMES.PPU]
            const { duration } = state.sharedInputs
            if (this.isPlannedUtilizationSetByMonthValid) {
                const totalUtilization = expected_utilization_by_month.reduce(
                    (sum, val) => sum + val,
                    0
                )
                const factor = 10 ** PLANNED_UTILIZATION_YEARLY_DECIMALS
                return Math.round((totalUtilization / duration) * factor) / factor
            }
            return state[PRODUCT_TYPE_NAMES.PPU].expected_utilization
        },

        activeProductTypeLabel(state) {
            const optionsStore = this._optionsStore()
            return optionsStore.getLabelByValue('productType')(state.activeProductType)
        },

        areAllInputsValid(state) {
            const requiredKeys = VALIDITY_KEYS_BY_PRODUCT[this.activeProductTypeLabel]

            if (!requiredKeys || requiredKeys.length === 0) {
                console.error(
                    `No validity keys defined for ${this.activeProductTypeLabel} product type, considering form as invalid (stores/caf/areAllInputsValid)`
                )
                return false
            }

            return requiredKeys.every((key) => {
                if (key === 'isExpectedUtilizationValid') {
                    return (
                        state.sectionValidity.isExpectedUtilizationValid ||
                        state.sectionValidity.isExpectedUtilizationByMonthValid
                    )
                }
                return state.sectionValidity[key] === true
            })
        },

        isPpuProductType() {
            return this.activeProductTypeLabel === PRODUCT_TYPE_NAMES.PPU
        },

        isFixedProductType() {
            return this.activeProductTypeLabel === PRODUCT_TYPE_NAMES.FIXED
        },

        isSlbProductType() {
            return this.activeProductTypeLabel === PRODUCT_TYPE_NAMES.SLB
        },
    },

    actions: {
        _userStore() {
            return useUserStore()
        },

        _optionsStore() {
            return useOptionsStore()
        },

        setBeginToNextMonth() {
            if (!isValid(this.sharedInputs.begin)) {
                const todayNextMonth = addMonths(new Date(), 1)
                this.sharedInputs.begin = startOfMonth(todayNextMonth)
            }
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        async downloadCalculation() {
            this.downloading = true
            try {
                if (!this.hasCurrentContract) {
                    const data = await this.saveContract()
                    if (!data?.id) return
                }
                const { preferred_locale } = this._userStore().userData
                const enEU = 'en-EU'
                const calculationPdfLanguage =
                    preferred_locale === enEU ? LOCALE_NAMES.enIE : preferred_locale
                await ContractService.downloadPdf({
                    locale: calculationPdfLanguage,
                    contract: this.contractId,
                })
            } catch (error) {
                console.error(
                    `Error in caf store downloadCalculation method for contract: ${this.contractId} (stores/caf/downloadCalculation):`,
                    error
                )
                this.notification('error', '§ui_message.download_failed')
            } finally {
                this.downloading = false
            }
        },

        registerFocusHandler(fieldId, handler) {
            this.focusHandlers[fieldId] = handler
        },

        unregisterFocusHandler(fieldId) {
            delete this.focusHandlers[fieldId]
        },

        focusField(fieldId) {
            if (this.focusHandlers[fieldId]) {
                this.focusHandlers[fieldId]()
            }
        },

        resetState() {
            this.$reset()
        },

        applyFormDefaults(isWashtec = false) {
            const { currency_code, preferred_number_format } = this._userStore().userData
            const { productTypeOptions, billingUnitOptions } = this._optionsStore()

            this.sharedInputs.maintenance_cost_show_as = isWashtec ? 'ppu_fee' : 'line_item'
            this.sharedInputs.currency_code = currency_code
            this.sharedInputs.preferred_number_format = preferred_number_format

            // the form's mounted hooks set these on the first visit only; a reset needs them again
            this.setBeginToNextMonth()
            if (this.activeProductType === null && productTypeOptions.length > 0) {
                this.activeProductType = productTypeOptions[0].value
            }
            if (this[PRODUCT_TYPE_NAMES.PPU].billing_unit === null) {
                this.billingUnitWatcher(billingUnitOptions, isWashtec)
            }

            this.formDefaultsApplied = true
        },

        invalidateContract() {
            this.contractId = null
            this.savedInputsKey = null
        },

        setValid(name) {
            this.sectionValidity[name] = true
            this.changeFormValidity()
        },

        setInvalid(name) {
            this.sectionValidity[name] = false
            this.changeFormValidity()
        },

        changeFormValidity() {
            if (this.areAllInputsValid) {
                this.formStatus = FORM_STATUS.valid
                this.scheduleCalculation()
                return
            }
            this.formStatus = FORM_STATUS.incomplete
        },

        handle_additional_form_errors({ error, remove }) {
            if (remove) {
                this.additionalFormErrors = this.additionalFormErrors.filter(
                    (el) => el.label !== error.label
                )
            } else if (!this.additionalFormErrors.map((el) => el.label).includes(error.label)) {
                this.additionalFormErrors = [...this.additionalFormErrors, error]
            }
        },

        amountWatcher(newValue) {
            if (newValue >= 1) {
                this.setValid('isAmountValid')

                if (Boolean(this.sharedInputs.buy_back)) {
                    this.residualValueWatcher(this.sharedInputs.residual_value)
                }

                return
            }

            this.setInvalid('isAmountValid')
        },

        residualValueWatcher(newValue) {
            if (Number(newValue) > 0 && this.sharedInputs.net_list_price >= Number(newValue)) {
                this.setValid('isResidualValueValid')
                return
            }

            if (this.sharedInputs.net_list_price < Number(newValue)) {
                this.setInvalid('isResidualValueValid')
            }
        },

        buyBackWatcher(newValue) {
            if (newValue == 0) {
                this.setValid('isBuyBackValid')
                this.setValid('isResidualValueValid')
            } else if (newValue == 1) {
                this.setValid('isBuyBackValid')
                this.setInvalid('isResidualValueValid')

                const isUtilizationValidOrNotRequired =
                    !this.isPpuProductType ||
                    this.sectionValidity.isExpectedUtilizationValid ||
                    this.sectionValidity.isExpectedUtilizationByMonthValid

                if (
                    this.sharedInputs.unit_type !== null &&
                    isUtilizationValidOrNotRequired &&
                    this.sharedInputs.duration > 0
                ) {
                    this.fetchAndSetResidualValue()
                }
            } else {
                this.setInvalid('isBuyBackValid')
            }
        },

        expectedUtilizationWatcher(newValue) {
            if (newValue > 0 && this[PRODUCT_TYPE_NAMES.PPU].unit_type_max_usage === null) {
                this.setValid('isExpectedUtilizationValid')
            } else if (
                newValue > 0 &&
                this.sharedInputs.unit_type !== null &&
                newValue <= this[PRODUCT_TYPE_NAMES.PPU].unit_type_max_usage
            ) {
                this.setValid('isExpectedUtilizationValid')
            } else {
                this.setInvalid('isExpectedUtilizationValid')
            }

            if (
                this.isPpuProductType &&
                Number(this.sharedInputs.buy_back) === 1 &&
                this.sharedInputs.unit_type !== null &&
                newValue > 0 &&
                this.sharedInputs.duration > 0
            ) {
                this.fetchAndSetResidualValue()
            }
        },

        durationWatcher(newValue, oldValue) {
            if (newValue >= 3 && newValue <= 10) {
                this.setValid('isDurationValid')
            } else {
                this.setInvalid('isDurationValid')
            }

            if (oldValue > newValue && this.isPlannedUtilizationSetByMonth) {
                this[PRODUCT_TYPE_NAMES.PPU].expected_utilization_by_month = this[
                    PRODUCT_TYPE_NAMES.PPU
                ].expected_utilization_by_month.slice(0, newValue * MONTHS_IN_YEAR)
            }

            const isUtilizationValidOrNotRequired =
                !this.isPpuProductType ||
                this.sectionValidity.isExpectedUtilizationValid ||
                this.sectionValidity.isExpectedUtilizationByMonthValid

            if (
                Number(this.sharedInputs.buy_back) === 1 &&
                this.sharedInputs.unit_type !== null &&
                isUtilizationValidOrNotRequired &&
                newValue > 0
            ) {
                this.fetchAndSetResidualValue()
            }
        },

        billingUnitWatcher(billingUnitOptions, isWashtec = false) {
            let value

            if (isWashtec) {
                const washOption = billingUnitOptions.find((obj) =>
                    obj.label.toLowerCase().includes('wash')
                )
                value = washOption ? washOption.value : undefined
            } else {
                const firstOption = billingUnitOptions.at(0)
                value = firstOption ? firstOption.value : undefined
            }

            if (value !== undefined) {
                const decimalsOption = this._optionsStore().billingUnitDecimalsOptions.find(
                    (item) => +item.label === +value
                )
                this[PRODUCT_TYPE_NAMES.PPU].billing_unit = value
                this[PRODUCT_TYPE_NAMES.PPU].billing_unit_decimals = decimalsOption
                    ? decimalsOption.value
                    : 2
            }
        },

        productTypeWatcher() {
            this.invalidateContract()
            this.formStatus = FORM_STATUS.incomplete
            this.changeFormValidity()
        },

        countryWatcher(newValue) {
            if (newValue > 0) {
                this.setValid('isCountryValid')
            } else {
                this.setInvalid('isCountryValid')
            }
        },

        expectedUtilizationArrayWatcher() {
            const { expected_utilization_by_month, unit_type_max_usage } =
                this[PRODUCT_TYPE_NAMES.PPU]
            const { duration } = this.sharedInputs
            const expectedMonths = duration * MONTHS_IN_YEAR

            if (
                !Array.isArray(expected_utilization_by_month) ||
                expected_utilization_by_month.length === 0
            ) {
                this.setInvalid('isExpectedUtilizationByMonthValid')
                return
            }

            const utilizationByYear = expected_utilization_by_month.reduce(
                (result, value, index) => {
                    const yearIndex = Math.floor(index / MONTHS_IN_YEAR)
                    if (!result[yearIndex]) result[yearIndex] = 0
                    result[yearIndex] += value
                    return result
                },
                []
            )

            if (
                expected_utilization_by_month.length === expectedMonths &&
                expected_utilization_by_month.every((val) => val > 0) &&
                utilizationByYear.every(
                    (yearlyUsage, index) =>
                        this.sharedInputs.unit_type === null ||
                        unit_type_max_usage === null ||
                        yearlyUsage <= unit_type_max_usage * (index + 1)
                )
            ) {
                this.setInvalid('isExpectedUtilizationValid')
                this.setValid('isExpectedUtilizationByMonthValid')
            } else {
                this.setInvalid('isExpectedUtilizationByMonthValid')
            }
        },

        scheduleCalculation: _.debounce(function () {
            if (this.formStatus === 'valid') {
                this.performCalculation()
            }
        }, 1000),

        getExpectedUtilization() {
            const { expected_utilization, expected_utilization_by_month } =
                this[PRODUCT_TYPE_NAMES.PPU]
            const { duration } = this.sharedInputs

            if (this.isPlannedUtilizationSetByMonthValid) {
                return expected_utilization_by_month
            }

            const forPeriod = duration * MONTHS_IN_YEAR
            return Array(forPeriod).fill(expected_utilization / MONTHS_IN_YEAR)
        },

        buildSaveInputs() {
            const inputs = {
                product_type: this.activeProductType,
                ...this.sharedInputs,
                ...this[this.activeProductTypeLabel],
            }

            if (this.isPpuProductType) {
                inputs.monthly_utilization_plan = this.getExpectedUtilization()
            }

            if (inputs.begin === '') {
                inputs.begin = startOfMonth(addMonths(new Date(), 1))
            }

            return inputs
        },

        async performCalculation() {
            this.calculating = true
            try {
                const inputs = toRaw(this.buildSaveInputs())

                const { data } = await CafService.calculate(inputs)
                const optionsStore = this._optionsStore()
                const productTypeLabel = optionsStore.getLabelByValue('productType')(
                    data.product_type
                )

                if (this.isPpuProductType) {
                    this.calculation[productTypeLabel] = { split_results: data.split_results }
                } else {
                    this.calculation[productTypeLabel] = { time_fee_per_m: data.time_fee_per_m }
                }

                this.formStatus = FORM_STATUS.calculated

                trackTimeSince('form_ts', 'calculation_generated', 'time_since_form_start', {
                    calculation_type: productTypeLabel,
                    result_summary: this.isPpuProductType
                        ? data.split_results
                        : data.time_fee_per_m,
                })
                trackHotjarEvent('calculation_generated')
            } catch (error) {
                console.error('Error calculating:', error)
                this.notification('error', '§ui_message.calculation_failed')
            } finally {
                this.calculating = false
            }
        },

        async saveContract() {
            this.saving = true
            try {
                const inputs = this.buildSaveInputs()
                const key = JSON.stringify(inputs)

                const { data } = await CafService.saveContract(inputs)
                this.contractId = data.id
                this.savedInputsKey = key
                this.notification('success', '§ui_message.contract_saved')
                return data
            } catch (error) {
                console.error('Error saving contract (stores/caf/saveContract):', error)
                this.notification('error', '§ui_message.save_failed')
            } finally {
                this.saving = false
            }
        },

        async saveContractWithConfirm() {
            if (isActAsOemActive(this._userStore())) {
                const confirmed = await useModalStore().setOpenWithPromise({
                    name: 'ConfirmActionModal',
                    size: 'small',
                    title: '§caf.confirm_save_contract_modal',
                    data: {
                        type: 'confirm',
                        buttonLabel: '§ui_button.confirm_save_contract_modal',
                    },
                })
                if (!confirmed) return null
            }

            if (!this._userStore().can('save calculation')) return null

            if (this.hasCurrentContract) return this.contractId

            const data = await this.saveContract()
            return data?.id ?? null
        },

        async getResidualValue() {
            this.calculatingResidualValue = true
            try {
                const payload = {
                    unit_type: this.sharedInputs.unit_type,
                    duration: this.sharedInputs.duration,
                    product_type: this.activeProductType,
                }

                if (this.isPpuProductType) {
                    payload.expected_utilization = this.getExpectedUtilization()
                }

                const { data } = await CafService.getResidualValue(payload)
                return data
            } catch (error) {
                console.error('Error getting residual value (stores/caf/getResidualValue):', error)
                this.notification('error', '§ui_message.load_failed')
            } finally {
                this.calculatingResidualValue = false
            }
        },

        fetchAndSetResidualValue: _.debounce(async function () {
            const data = await this.getResidualValue()
            if (data && !Number.isNaN(data.residual_value)) {
                const { residual_value } = data
                if (
                    !this.sharedInputs.residual_value ||
                    this.sharedInputs.residual_value === this.sharedInputs.stored_residual_value
                ) {
                    this.sharedInputs.residual_value = residual_value
                }
                this.sharedInputs.stored_residual_value = residual_value
            }
        }, 1000),

        async getItemList(route) {
            this.loading = true
            try {
                const { data } = await CafService.getItemList(route)
                return data
            } catch (error) {
                console.error(
                    `Error in click-and-finance fetching ${route} list (stores/caf/getItemList):`,
                    error
                )
                this.notification('error', '§ui_message.load_failed')
            } finally {
                this.loading = false
            }
        },
    },
})
